import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";

const TZ_OFFSET = new Date().getTimezoneOffset();
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function PresetSchedule({ preset, onSaved }) {
  const s = preset.schedule || {};
  const [cfg, setCfg] = useState({
    enabled: !!s.enabled,
    frequency: s.frequency || "daily",
    weekday: String(s.weekday ?? 0),
    time_hhmm: s.time_hhmm || "02:00",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async (next = cfg) => {
    setBusy(true);
    try {
      await api.setPresetSchedule(preset.id, {
        enabled: next.enabled,
        frequency: next.frequency,
        weekday: Number(next.weekday),
        time_hhmm: next.time_hhmm,
        tz_offset_minutes: TZ_OFFSET,
      });
      toast.success(next.enabled
        ? `"${preset.name}" runs ${next.frequency === "weekly" ? `every ${DAYS[Number(next.weekday)]}` : "daily"} at ${next.time_hhmm}`
        : "Auto-run disabled");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save schedule");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-1.5 rounded border border-border bg-[#0B0F18] px-2.5 py-2 space-y-2"
      data-testid={`preset-schedule-${preset.id}`}>
      <div className="flex items-center gap-2">
        <CalendarClock className="h-3.5 w-3.5 text-purple-400" />
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Auto-run</span>
        <Switch className="ml-auto" data-testid={`preset-schedule-toggle-${preset.id}`} checked={cfg.enabled}
          onCheckedChange={(v) => { set("enabled", v); save({ ...cfg, enabled: v }); }} />
      </div>

      {cfg.enabled && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Select value={cfg.frequency} onValueChange={(v) => set("frequency", v)}>
            <SelectTrigger data-testid={`preset-frequency-${preset.id}`}
              className="h-7 w-[104px] bg-[#0E1420] border-border font-mono text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Nightly</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
          {cfg.frequency === "weekly" && (
            <Select value={cfg.weekday} onValueChange={(v) => set("weekday", v)}>
              <SelectTrigger data-testid={`preset-weekday-${preset.id}`}
                className="h-7 w-[118px] bg-[#0E1420] border-border font-mono text-[11px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input data-testid={`preset-time-${preset.id}`} value={cfg.time_hhmm}
            onChange={(e) => set("time_hhmm", e.target.value)} placeholder="02:00"
            className="h-7 w-[70px] bg-[#0E1420] border-border font-mono text-[11px]" />
          <Button size="sm" data-testid={`save-preset-schedule-${preset.id}`} onClick={() => save()} disabled={busy}
            className="h-7 bg-purple-500/90 hover:bg-purple-400 text-slate-950 font-semibold text-[11px]">Save</Button>
          <span className="font-mono text-[9px] text-slate-600 w-full">
            {s.last_run_at ? `last auto-run ${s.last_run_at.slice(0, 16).replace("T", " ")} UTC` : "never auto-run yet"}
            {" · launches a fresh job, respects your plan quota"}
          </span>
        </div>
      )}
    </div>
  );
}
